import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { queryKeys } from '@/api/queryKeys';
import { get, post } from '@/api/client';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import { Skeleton } from '@/components/atoms/Skeleton';
import { comma } from '@/lib/format';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { toast } from '@/store/toastStore';
import { useScrollLock } from '@/hooks/useScrollLock';
import { POINT_POLICY } from '@/constants/points';

/** 간단한 이메일 형식 검증 — 로컬@도메인.tld 구조 */
function isValidEmail(email: string): boolean {
  const trimmed = email.trim();
  if (trimmed.length < 5 || trimmed.length > 254) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
}

interface Item {
  code: string;
  name: string;
  point_amount: number;
  type: string;
  image_url: string | null;
  description: string;
}

export function ExchangePage() {
  const queryClient = useQueryClient();
  const { me, isLoggedIn } = useAuthStore();
  const setLoginModalOpen = useUIStore((s) => s.setLoginModalOpen);
  const [selected, setSelected] = useState<Item | null>(null);
  const [recipient, setRecipient] = useState('');
  const [storeName, setStoreName] = useState<'' | 'CU' | 'GS25' | '세븐일레븐'>('');
  /* 신청 직후 결과 화면 — 선택한 편의점/상품을 한 번 더 확인할 수 있도록 잠시 띄움 */
  const [submittedSummary, setSubmittedSummary] = useState<
    | { itemName: string; storeName: string | null; pointAmount: number }
    | null
  >(null);
  useScrollLock(!!selected || !!submittedSummary);

  const { data: itemsData, isLoading } = useQuery({
    queryKey: queryKeys.exchange.items,
    queryFn: () => get<{ items: Item[] }>('/exchange/items'),
  });

  const { data: balanceData } = useQuery({
    queryKey: queryKeys.point.me,
    queryFn: () => get<{ balance: number }>('/points/me'),
  });

  const mutation = useMutation({
    mutationFn: () =>
      post('/exchange', {
        itemCode: selected!.code,
        recipient,
        // 편의점 상품일 때만 storeName 전달 — 그 외에는 null
        storeName: selected!.type === 'convenience' ? storeName || null : null,
      }),
    onSuccess: () => {
      toast.success('교환 신청이 완료되었어요!');
      setSubmittedSummary({
        itemName: selected!.name,
        storeName: selected!.type === 'convenience' ? storeName || null : null,
        pointAmount: selected!.point_amount,
      });
      setSelected(null);
      setRecipient('');
      setStoreName('');
      queryClient.invalidateQueries({ queryKey: queryKeys.point.me });
      queryClient.invalidateQueries({ queryKey: queryKeys.exchange.history });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message ?? '교환 신청에 실패했어요.');
    },
  });

  const balance = balanceData?.balance ?? 0;
  const MIN_BALANCE = POINT_POLICY.minExchangeBalance;
  const eligible = balance >= MIN_BALANCE;

  return (
    <div className="container-base py-10">
      {/* 잔액 표시 */}
      <section className="mb-6 rounded-2xl bg-gradient-to-br from-[var(--color-primary)] to-[#2a4f7a] p-6 text-white">
        <div className="text-sm opacity-80">보유 포인트 (1P = 1원)</div>
        <div className="mt-1 text-4xl font-extrabold">{comma(balance)}P</div>
        <div className="mt-2 text-xs opacity-80">
          {me?.nickname}님, 포인트를 상품으로 교환해 보세요!
        </div>
      </section>

      {!eligible && (
        <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          <strong>{comma(MIN_BALANCE)}P 이상 보유 시 교환 가능합니다.</strong>{' '}
          현재 잔액 {comma(balance)}P — {comma(Math.max(0, MIN_BALANCE - balance))}P 가 더 필요해요.
        </div>
      )}

      <h2 className="mb-4 section-title">교환 가능 상품</h2>

      {isLoading ? (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-40 w-full" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          {itemsData?.items?.map((item) => {
            const canAfford = eligible && balance >= item.point_amount;
            return (
              <button
                key={item.code}
                onClick={() => {
                  if (!isLoggedIn) return setLoginModalOpen(true);
                  if (canAfford) setSelected(item);
                }}
                disabled={!canAfford}
                className={`rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 text-left transition-all ${
                  canAfford
                    ? 'cursor-pointer hover:border-[var(--color-primary)]/40'
                    : 'opacity-50 cursor-not-allowed'
                }`}
              >
                <div className="text-3xl">🎁</div>
                <div className="mt-2 text-sm font-semibold line-clamp-2">{item.name}</div>
                <div className="mt-2 text-[var(--color-primary)] font-bold">
                  {comma(item.point_amount)}P
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* 교환 확인 모달 */}
      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-4"
          onClick={() => setSelected(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl bg-[var(--color-surface)] p-5 sm:p-6"
          >
            <h3 className="text-xl font-bold">{selected.name}</h3>
            <p className="mt-2 text-sm text-[var(--color-text-sub)]">{selected.description}</p>
            <div className="mt-4 rounded-lg bg-[var(--color-bg)] p-3">
              <div className="flex justify-between text-sm">
                <span>차감 포인트</span>
                <span className="font-bold text-[var(--color-primary)]">
                  -{comma(selected.point_amount)}P
                </span>
              </div>
              <div className="mt-1 flex justify-between text-xs text-[var(--color-text-sub)]">
                <span>남은 포인트</span>
                <span>{comma(balance - selected.point_amount)}P</span>
              </div>
            </div>

            {/* 유효기간 / 환불 정책 안내 — 승인 후 29일 미수령은 환불 불가 */}
            <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs leading-relaxed text-amber-900">
              <strong>교환 승인 후 쿠폰번호는 29일 이내에 확인 가능합니다.</strong>
              <br />
              상품권 유효기간은 발급일로부터 약 29~30일이며, 유효기간 내 미사용 시 소멸되며 환불되지 않습니다.
              <br />
              신청 전 반드시 확인해주세요. (운영자 거절 시에만 자동 환불)
            </div>
            {selected.type === 'convenience' && (
              <div className="mt-4">
                <label className="mb-1 block text-sm font-medium">
                  편의점 선택 <span className="text-[var(--color-fall)]">(필수)</span>
                </label>
                <select
                  value={storeName}
                  onChange={(e) => setStoreName(e.target.value as typeof storeName)}
                  className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm"
                  aria-invalid={!storeName}
                >
                  <option value="">선택해주세요</option>
                  <option value="CU">CU</option>
                  <option value="GS25">GS25</option>
                  <option value="세븐일레븐">세븐일레븐</option>
                </select>
                {!storeName && (
                  <p className="mt-1 text-xs text-[var(--color-text-sub)]">
                    선택한 편의점의 모바일 상품권으로 발급돼요.
                  </p>
                )}
              </div>
            )}
            <div className="mt-4">
              <label className="mb-1 block text-sm font-medium">
                수령 이메일{' '}
                <span className="text-[var(--color-fall)]">(필수)</span>
              </label>
              <Input
                type="email"
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                placeholder="example@email.com"
                aria-invalid={recipient.length > 0 && !isValidEmail(recipient)}
              />
              {recipient.length > 0 && !isValidEmail(recipient) && (
                <p className="mt-1 text-xs text-[var(--color-fall)]">
                  올바른 이메일 주소를 입력해주세요.
                </p>
              )}
            </div>
            <div className="mt-6 flex gap-2">
              <Button
                variant="ghost"
                fullWidth
                onClick={() => {
                  setSelected(null);
                  setStoreName('');
                }}
              >
                취소
              </Button>
              <Button
                fullWidth
                loading={mutation.isPending}
                disabled={
                  !isValidEmail(recipient) ||
                  (selected.type === 'convenience' && !storeName)
                }
                onClick={() => mutation.mutate()}
              >
                교환 신청
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 신청 완료 — 어떤 편의점/상품으로 신청됐는지 확인 */}
      {submittedSummary && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-4"
          onClick={() => setSubmittedSummary(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl bg-[var(--color-surface)] p-5 sm:p-6 text-center"
          >
            <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-[var(--color-primary)]/10 text-3xl">
              ✓
            </div>
            <h3 className="text-xl font-bold">교환 신청 완료</h3>
            <p className="mt-1 text-sm text-[var(--color-text-sub)]">
              운영자 승인 후 메일로 발송돼요.
            </p>
            <dl className="mt-5 space-y-2 text-left text-sm rounded-lg bg-[var(--color-bg)] p-4">
              <div className="flex justify-between">
                <dt className="text-[var(--color-text-sub)]">상품</dt>
                <dd className="font-semibold">{submittedSummary.itemName}</dd>
              </div>
              {submittedSummary.storeName && (
                <div className="flex justify-between">
                  <dt className="text-[var(--color-text-sub)]">편의점</dt>
                  <dd className="font-semibold">{submittedSummary.storeName}</dd>
                </div>
              )}
              <div className="flex justify-between">
                <dt className="text-[var(--color-text-sub)]">차감 포인트</dt>
                <dd className="font-bold text-[var(--color-primary)]">
                  -{comma(submittedSummary.pointAmount)}P
                </dd>
              </div>
            </dl>
            <Button fullWidth className="mt-6" onClick={() => setSubmittedSummary(null)}>
              확인
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
